import * as Sentry from '@sentry/browser';
import { ExtraErrorData as ExtraErrorDataIntegration } from '@sentry/integrations';
import get from 'lodash/get';
import identity from 'lodash/identity';
import map from 'lodash/map';
import pickBy from 'lodash/pickBy';
import some from 'lodash/some';
import truncate from 'lodash/truncate';
import { SENTRY_DSN, ENVIRONMENT, VERSION } from 'config/env';
import findOrCreateQhUserId from './lib/findOrCreateQhUserId';

const ignoredErrors = [
  /InvalidStateError: InvalidStateError/i,
  /webkitExitFullScreen/i,
  /Out of memory/i,
  /Loading chunk/i,
  /Error in \$A\.getCallback\(\) \[Expected identifier\]/i,
  /Failed to construct 'Notification'/i,
  /Unexpected token '?<'?/i,
  /expected expression, got '?<'?/,
  /Cannot redefine property: inert/i,
  /Failed to execute 'getBattery'/i,
  /400: Bad Request/i,
  /401: Unauthorized/i,
  /403: Forbidden/i,
  /404: Not Found/i,
  /422: Unprocessable Entity/i,
  /Request failed with status code 401/i,
  // TODO:  Remove when below UnhandledRejection with `Object Not Found Matching Id` been resolved/fix https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/17
  /UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:/i,
  /Cannot read property 'default' of undefined/i,
  /null is not an object \(evaluating 'h.tagName'\)/,
];

const networkPatterns = [
  /(services|accounts)(.*)\.qantas/,
  /demdex/,
  /celebrus/,
  /xg4ken/,
  /doubleclick/,
  /hotjar/,
  /google-analytics/,
  /-extension/,
];

export const breadcrumbFiltering: Sentry.BrowserOptions['beforeBreadcrumb'] = (breadcrumb, hint) => {
  // Filter live chat warnings
  if (/Embedded Service Chat may not function correctly with this native JS function modified/.test(breadcrumb.message!)) return null;

  // Filter Auth widget console logs
  if (/QLoginWidget/.test(breadcrumb.message!)) return null;

  // Filter simplicity initialisation
  if (breadcrumb.message === 'ul.primary_navigation. > li.primary_navigation_item._focus_removed') return null;

  // Filter 3rd party network requests
  const url = get(breadcrumb, 'data.url');
  if (url && some(networkPatterns, (pattern) => url.match(pattern))) return null;

  // Better breadcrumbs on click
  if (breadcrumb.category === 'ui.click') {
    try {
      const target = hint?.event.target;
      const data = {
        tag: `<${target.tagName.toLowerCase()}>`,
        title: target.getAttribute('title'),
        testId: target.getAttribute('data-testid'),
        altText: target.getAttribute('alt'),
        text: truncate(target.textContent, { length: 50 }),
      };
      breadcrumb.message = map(pickBy(data, identity), (v, k) => `${k}: ${v}`).join(', ');
    } catch (error) {
      return null;
    }
  }

  return breadcrumb;
};

if (SENTRY_DSN && VERSION && ['production', 'staging', 'sit'].includes(ENVIRONMENT)) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // Try to get more info https://docs.sentry.io/platforms/javascript/configuration/integrations/plugin/#extraerrordata
    integrations: [new ExtraErrorDataIntegration({ depth: 3 })],
    environment: ENVIRONMENT,
    release: VERSION,
    ignoreErrors: ignoredErrors,
    maxBreadcrumbs: 100,
    beforeBreadcrumb: breadcrumbFiltering,
  });

  Sentry.configureScope((scope) => scope.setUser({ id: findOrCreateQhUserId() }));
} else {
  console.log('Sentry disabled'); // eslint-disable-line no-console
}
